import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Logo } from '@/components/ui/logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { signIn, signUp } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import { ResetPasswordDialog } from '@/components/auth/reset-password-dialog';
import { RegistrationConfirmation } from '@/components/auth/registration-confirmation';
import {
  supportEmail,
  IS_HARTZORG,
  HOOFDNAARHART_ORIGIN,
  LOGIN_LEGAL_DOCUMENTS,
  MARKETING_URL,
} from '@/lib/constants';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { RichTextViewer } from '@/components/ui/rich-text-viewer';

interface LegalDocument {
  id: string;
  name: string;
  content: string;
}

export default function HoofdHartLoginPage() {
  const { profile } = useProfile();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteParam = searchParams.get('invite');
  const signupParam = searchParams.get('signup');

  // Where to land after signing in. Set by the samen invite page so the join
  // link survives a detour through the login form. Only same-site paths are
  // honoured -- an absolute URL here would be an open redirect.
  const nextParam = searchParams.get('next');
  const nextTarget =
    nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')
      ? nextParam
      : '/';
  const [isLogin, setIsLogin] = useState(signupParam !== '1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [canChangeInviteCode, setCanChangeInviteCode] = useState(!inviteParam);
  const [legalDocs, setLegalDocs] = useState<LegalDocument[]>([]);
  const [viewingDoc, setViewingDoc] = useState<LegalDocument | null>(null);

  const isCapacitor = !!(window as any).Capacitor;
  const hideLogo = isCapacitor && !isLogin;

  useEffect(() => {
    // This is the HoofdnaarHart variant's login page. The Hartzorg build has
    // its own, so nobody should land here.
    //
    // On hartzorg.app it used to be the only way to reach the HoofdnaarHart
    // login, so bookmarks and old links still point at it — send those across
    // to the app that now owns this page rather than to the Hartzorg login,
    // which is the wrong app entirely. In the native app, and anywhere else
    // (a dev server, a preview build), fall back to /login: there is no other
    // app to hand off to.
    if (IS_HARTZORG) {
      const onHartzorgWeb =
        !isCapacitor &&
        /^(www\.)?hartzorg\.app$/.test(window.location.hostname);

      if (onHartzorgWeb) {
        window.location.replace(`${HOOFDNAARHART_ORIGIN}/hoofdhartlogin`);
      } else {
        navigate('/login', { replace: true });
      }
      return;
    }
    if (profile) navigate(nextTarget);
  }, [profile]);

  useEffect(() => {
    supabase
      .from('legal_documents')
      .select('id, name, content')
      .eq('role_scope', 'hoofdhartuser')
      .in('name', LOGIN_LEGAL_DOCUMENTS)
      .then(({ data }) => {
        if (data) setLegalDocs(data);
      });
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    const curInviteCode = isLogin
      ? null
      : (e.currentTarget.querySelector('#inviteCode') as HTMLInputElement)?.value;

    try {
      if (isLogin) {
        const data = await signIn({ email, password });

        // Check that this user has the hoofdhartuser role
        const { data: roleData, error: roleError } = await supabase
          .from('profile_role')
          .select('role')
          .eq('user_id', data.user.id)
          .single();

        if (roleError || roleData?.role !== 'hoofdhartuser') {
          await supabase.auth.signOut();
          setError('Dit account heeft geen toegang tot deze login pagina.');
          setLoading(false);
          return;
        }

        window.location.href = nextTarget;
      } else {
        const confirmPassword = formData.get('confirmPassword') as string;

        if (password !== confirmPassword) {
          setError('Wachtwoorden komen niet overeen');
          setLoading(false);
          return;
        }

        await signUp({
          email,
          password,
          naam: formData.get('naam') as string,
          telefoonnummer: (formData.get('telefoonnummer') as string) || null,
          inviteCode: curInviteCode,
        });

        setRegisteredEmail(email);
        setShowConfirmation(true);
        setLoading(false);
        return;
      }
    } catch (err: any) {
      if (curInviteCode && err.message === 'Database error saving new user') {
        setError(
          `De uitnodigingscode is misschien ongeldig of verlopen. Neem contact op via ${supportEmail}`,
        );
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setCanChangeInviteCode(true);
    }
  }

  const RequiredStar = isLogin ? null : <span className="text-red-500">*</span>;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4 relative cap-login"
      style={{
        backgroundImage: 'url(/images/hoofdnaarhart-bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-black/30" />

      <div className="relative z-10 w-full max-w-md">
        <RegistrationConfirmation
          open={showConfirmation}
          onClose={() => {
            setShowConfirmation(false);
            setIsLogin(true);
          }}
          email={registeredEmail}
          variant="hoofdhart"
        />
        <ResetPasswordDialog
          open={showResetPassword}
          onClose={() => setShowResetPassword(false)}
          variant="hoofdhart"
        />
        <Card className="w-full max-w-md p-6 space-y-6 bg-white/80 backdrop-blur-md shadow-xl text-slate-900">
          {!hideLogo && (
            <Logo
              className="w-48 h-48 mx-auto"
              src="/images/hoofdnaarhart-logo.png"
            />
          )}

          <div className="space-y-2 text-center">
            <h2 className="text-2xl font-bold">
              {isLogin ? 'Inloggen' : 'Registreren'}
            </h2>
            <p className="text-slate-600">
              {isLogin ? 'Welkom terug' : 'Maak je account aan'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="inviteCode">
                    Uitnodigingscode <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="inviteCode"
                    name="inviteCode"
                    type="text"
                    placeholder="Voer je uitnodigingscode in"
                    defaultValue={inviteParam || ''}
                    disabled={!canChangeInviteCode}
                    required
                    autoFocus={canChangeInviteCode}
                  />
                </div>
                <hr className="my-4 border-gray-300" />
                <div className="space-y-2">
                  <Label htmlFor="naam">Naam {RequiredStar}</Label>
                  <Input id="naam" name="naam" required autoFocus={!canChangeInviteCode} />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email {RequiredStar}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                autoFocus={isLogin}
              />
            </div>

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="telefoonnummer">Telefoonnummer</Label>
                <Input id="telefoonnummer" name="telefoonnummer" type="tel" />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord {RequiredStar}</Label>
              <Input id="password" name="password" type="password" required />
            </div>

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">
                  Bevestig wachtwoord <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                />
              </div>
            )}

            {error && <p className="text-sm text-red-500">{error}</p>}

            <Button
              type="submit"
              className="w-full rounded-full bg-red-700 hover:bg-red-600 text-white"
              disabled={loading}
            >
              {loading ? 'Laden...' : isLogin ? 'Inloggen' : 'Registreren'}
            </Button>
          </form>

          <div className="text-center space-y-2">
            <Button
              variant="link"
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="text-sm text-slate-900"
            >
              {isLogin
                ? 'Nog geen account? Registreer je'
                : 'Al een account? Log in'}
            </Button>
            {isLogin && (
              <div>
                <Button
                  variant="link"
                  onClick={() => setShowResetPassword(true)}
                  className="text-sm text-slate-900"
                >
                  Wachtwoord vergeten?
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>

      <div className="relative z-10 mt-4 flex flex-wrap justify-center gap-4">
        {legalDocs.map((doc) => (
          <button
            key={doc.id}
            onClick={() => setViewingDoc(doc)}
            className="text-sm text-white/80 hover:text-white underline"
          >
            {doc.name}
          </button>
        ))}
        {/* New tab on purpose: the native builds are a webview with no back
            button, so navigating away would strand someone at the login. */}
        <a
          href={MARKETING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-white/80 hover:text-white underline"
        >
          Meer over ons
        </a>
      </div>

      <Dialog
        open={viewingDoc !== null}
        onOpenChange={(open) => { if (!open) setViewingDoc(null); }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{viewingDoc?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto min-h-0 py-4">
            {viewingDoc?.content ? (
              <RichTextViewer content={viewingDoc.content} />
            ) : (
              <p className="text-muted-foreground">Geen inhoud beschikbaar.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
